import type { PreferenceCenterSubscription } from '@agentselly/immodossier-shared';
import {
  AppAction,
  AppRoute,
  HubspotConfig,
} from '@agentselly/immodossier-shared';
import { useTranslate } from '@tolgee/react';
import { clsx } from 'clsx';
import { Card } from '@agentselly/ui';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { createFileRoute } from '@tanstack/react-router';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppParams } from '~/helper/useAppParams';
import { fetchBackend } from '~/helper/fetchBackend.ts';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_PREFERENCE_CENTER,
)({
  component: Body,
});

function Body() {
  const { t } = useTranslate();

  const params = useAppParams(Route);

  const [
    allDisabled,
    setAllDisabled,
  ] = useState(false);

  const [
    disabledSubscriptionIds,
    setDisabledSubscriptionIds,
  ] = useState<Set<number>>(new Set());

  const queryClient = useQueryClient();

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_PREFERENCE_CENTER,
    {
      params: params,
    },
  );

  if (query.isError) {
    return <ErrorWidget error={query.error} />;
  }

  function updateSubscription(
    event: ChangeEvent<HTMLInputElement> | any,
    subscriptionId: number,
    newValue: boolean,
  ) {
    event.preventDefault();

    if (newValue) {
      console.error(
        'If you OPT OUT of all emails or a specific email subscription type for a recipient, there is NO UNDO for this operation.',
      );

      alert(t('preferences_subscribtion_resubscribe'));

      return false;
    }

    if (subscriptionId === HubspotConfig.SubscriptionIdAll) {
      setDisabledSubscriptionIds(new Set());

      setAllDisabled(true);
    } else {
      setDisabledSubscriptionIds((prev) => {
        const next = new Set(prev);

        next.add(subscriptionId);

        return next;
      });
    }

    function undoDisable() {
      if (subscriptionId === HubspotConfig.SubscriptionIdAll) {
        setAllDisabled(false);
      } else {
        setDisabledSubscriptionIds((prev) => {
          const next = new Set(prev);

          next.delete(subscriptionId);

          return next;
        });
      }
    }

    fetchBackend(`/action/${AppAction.PreferencesUpdateSubscription}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contactId: parseInt(query.data?.contactId || '0'),
        subscriptionId: subscriptionId,
        newValue: newValue,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          console.error('Failed to update subscription', response);

          undoDisable();

          return;
        }

        await queryClient.invalidateQueries({
          queryKey: [
            AppRoute.$LANG_$COMPANYID_$CONTACTID_PREFERENCE_CENTER,
          ],
        });

        undoDisable();
      })
      .catch(console.error);

    return false;
  }

  return (
    <Card>
      <div
        className={clsx('p-6 flex flex-col gap-6 w-full', {
          'animate-pulse pointer-events-none': query.isLoading,
        })}
      >
        <h1 className="text-2xl">{t('preferences_your_preferences_title')}</h1>
        <p>
          {t('preferences_your_preferences_explanation', {
            email: query.data?.safeEmail,
          })}
        </p>
        {query.data?.isUnsubscribed ? (
          <div
            role="alert"
            className="alert alert-error"
          >
            <p>{t('preferences_unsubscribed_explanation')}</p>
          </div>
        ) : null}

        <h2 className="text-xl font-bold">{t('preferences_channels_title')}</h2>
        <div className="flex flex-col gap-2">
          {query.data?.channels?.map(
            (subscription: PreferenceCenterSubscription) => (
              <div
                key={subscription.id}
                className={clsx(
                  'flex justify-between items-center my-2 border-b-2 pb-2',
                  {
                    'opacity-50':
                      query.data?.isUnsubscribed || !subscription.isSubscribed,
                  },
                )}
              >
                <div className="flex flex-col mr-4">
                  <span className="text-lg">{subscription.name}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {subscription.description}
                  </span>
                </div>
                <input
                  type="checkbox"
                  onChange={(event) =>
                    updateSubscription(
                      event,
                      subscription.id,
                      !subscription.isSubscribed,
                    )
                  }
                  checked={subscription.isSubscribed}
                  className="toggle toggle-primary"
                  disabled={
                    query.data?.isUnsubscribed ||
                    allDisabled ||
                    disabledSubscriptionIds.has(subscription.id)
                  }
                />
              </div>
            ),
          )}
        </div>

        <h2 className="text-xl font-bold">
          {t('preferences_relevant_topics_title')}
        </h2>
        <div className="flex flex-col gap-2">
          {query.data?.subscriptions?.map(
            (subscription: PreferenceCenterSubscription) => (
              <div
                key={subscription.id}
                className={clsx(
                  'flex justify-between items-center my-2 border-b-2 pb-2',
                  {
                    'opacity-50':
                      query.data?.isUnsubscribed || !subscription.isSubscribed,
                  },
                )}
              >
                <div className="flex flex-col mr-4">
                  <span className="text-lg">{subscription.name}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {subscription.description}
                  </span>
                </div>
                <input
                  type="checkbox"
                  onChange={(event) =>
                    updateSubscription(
                      event,
                      subscription.id,
                      !subscription.isSubscribed,
                    )
                  }
                  checked={subscription.isSubscribed}
                  className="toggle toggle-primary"
                  disabled={
                    query.data?.isUnsubscribed ||
                    allDisabled ||
                    disabledSubscriptionIds.has(subscription.id)
                  }
                />
              </div>
            ),
          )}
        </div>

        <div className="mt-4">
          {query.data?.isUnsubscribed ? (
            <div
              role="alert"
              className="alert alert-error"
            >
              <p>{t('preferences_unsubscribed_noaction_note')}</p>
            </div>
          ) : (
            <button
              type="button"
              onClick={(event) =>
                updateSubscription(
                  event,
                  HubspotConfig.SubscriptionIdAll,
                  Boolean(query.data?.isUnsubscribed),
                )
              }
              className={clsx(
                'px-4 py-2 rounded-xl shadow dark:shadow-gray-900/50 cursor-pointer',
                {
                  'bg-primary-600 hover:bg-primary-800 text-white':
                    query.data?.isUnsubscribed,
                  'bg-red-600 hover:bg-red-800 text-white':
                    !query.data?.isUnsubscribed,
                  'bg-gray-400 hover:bg-gray-600 text-white':
                    allDisabled || disabledSubscriptionIds.size > 0,
                },
              )}
              disabled={allDisabled || disabledSubscriptionIds.size > 0}
            >
              {query.data?.isUnsubscribed
                ? t('preferences_resubscribe_to_all_link')
                : t('preferences_unsubscribe_from_all_link')}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}
